<template>
  <div class="detail-container">
    <!-- 顶部横幅 -->
    <TopHeader />

    <!-- 题目详情 -->
    <div class="main-wrapper">
      <div class="back-link" @click="goBack">
        <img src="@/assets/image/back.png" class="back-icon" />
        <span>返回收藏列表</span>
      </div>

      <div v-if="loading" class="loading-box">加载中...</div>
      
      <div v-else-if="!question" class="empty-box">题目不存在</div>

      <div v-else class="question-card">
        <div class="question-header">
          <div class="question-title">{{ question.title }}</div>
          <div class="question-right">
            <span :class="['question-type', question.type === 'single' ? 'single' : 'multi']">
              {{ question.type === 'single' ? '单选题' : '多选题' }}
            </span>
            <img 
              :src="question.favorited ? require('@/assets/image/star-select.png') : require('@/assets/image/star.png')" 
              class="favorite-icon" 
              @click="toggleFavorite"
            />
          </div>
        </div>

        <div class="options">
          <div 
            v-for="(opt, index) in question.options" 
            :key="index"
            :class="getOptionClass(index)"
            @click="selectOption(index)"
          >
            <span :class="['option-label', question.type === 'single' ? 'circle' : 'square']">{{ ['A', 'B', 'C', 'D', 'E', 'F'][index] }}</span>
            <span v-if="opt.content" class="option-text">{{ opt.content }}</span>
            <img v-else-if="opt.image" :src="baseURL + opt.image" class="option-image" @click.stop="showPreview(baseURL + opt.image)" />
          </div>
        </div>

        <!-- 多选确认按钮 -->
        <div v-if="question.type === 'multi' && !question.answered && question.selected.length > 0" class="confirm-multi">
          <button class="confirm-btn" @click="confirmMulti">确认答案</button>
        </div>

        <!-- 答案解析 -->
        <div v-if="question.answered" class="answer-section">
          <div class="answer-info">
            <span class="answer-label">正确答案：{{ getAnswerText(question.answer) }}</span>
            <span v-if="question.selected.length > 0" class="your-answer">
              你的答案：{{ getAnswerText(question.selected) }}
            </span>
          </div>
          <div class="answer-explain">{{ question.analysis }}</div>
        </div>

        <div class="question-meta">
          <span class="meta-item" v-if="question.collectTime">
            <img src="@/assets/image/star.png" class="meta-icon" />
            <span>收藏时间：{{ formatTime(question.collectTime) }}</span>
          </span>
          <span class="meta-item" v-if="question.sectionName">
            <img src="@/assets/image/kaoshi.png" class="meta-icon" />
            <span>{{ question.sectionName }}</span>
          </span>
        </div>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <div v-if="previewImage" class="image-preview-overlay" @click="previewImage = ''">
      <img :src="previewImage" class="preview-img" @click.stop />
    </div>
  </div>
</template>

<script>
import TopHeader from '@/components/TopHeader.vue'
import { getQuestionDetail, checkAnswer, toggleFavorite as toggleFavoriteApi } from '@/api/question'
import { decodeId } from '@/utils/idEncoder'
import config from '@/utils/config'

export default {
  name: 'FavoriteDetailPage',
  components: {
    TopHeader
  },
  data() {
    return {
      baseURL: config.imageBaseURL,
      loading: true,
      question: null,
      previewImage: ''
    }
  },
  created() {
    this.loadQuestion()
  },
  methods: {
    async loadQuestion() {
      this.loading = true
      const questionId = decodeId(this.$route.params.questionId)
      try {
        const res = await getQuestionDetail(questionId)
        const q = res.data
        this.question = {
          questionId: q.questionId,
          title: q.title,
          type: q.type,
          options: q.options,
          sectionId: q.sectionId,
          sectionName: q.sectionName,
          favorited: q.favorited,
          collectTime: q.collectTime,
          answer: [],
          analysis: '',
          selected: [],
          answered: false
        }
      } catch (error) {
        console.error('加载题目失败', error)
      } finally {
        this.loading = false
      }
    },
    goBack() {
      this.$router.push('/favorite')
    },
    async toggleFavorite() {
      try {
        const res = await toggleFavoriteApi(this.question.questionId)
        this.question.favorited = res.data
      } catch (error) {
        console.error('收藏操作失败', error)
      }
    },
    async selectOption(index) {
      if (this.question.answered) return
      
      if (this.question.type === 'single') {
        this.question.selected = [index]
        await this.doCheck()
      } else {
        const idx = this.question.selected.indexOf(index)
        if (idx > -1) {
          this.question.selected.splice(idx, 1)
        } else {
          this.question.selected.push(index)
        }
      }
    },
    async confirmMulti() {
      if (this.question.selected.length > 0) {
        await this.doCheck()
      }
    },
    async doCheck() {
      const userAnswer = this.question.selected.map(i => ['A', 'B', 'C', 'D', 'E', 'F'][i]).join(',')
      
      try {
        const res = await checkAnswer({
          questionId: this.question.questionId,
          userAnswer: userAnswer
        })
        
        this.question.answered = true
        this.question.analysis = res.data.analysis || '暂无解析'
        const correctLabels = res.data.correctAnswer.split(',')
        this.question.answer = correctLabels.map(label => label.charCodeAt(0) - 65)
      } catch (error) {
        console.error('校验答案失败', error)
      }
    },
    getOptionClass(index) {
      const classes = ['option']
      const isSelected = this.question.selected.includes(index)
      const isCorrect = this.question.answer.includes(index)
      
      if (this.question.answered) {
        if (isCorrect) {
          classes.push('correct')
        } else if (isSelected && !isCorrect) {
          classes.push('wrong')
        }
      } else if (isSelected) {
        classes.push('selected')
      }
      
      return classes
    },
    getAnswerText(arr) {
      return arr.map(i => ['A', 'B', 'C', 'D', 'E', 'F'][i]).join('、')
    },
    formatTime(time) {
      if (!time) return ''
      const d = new Date(time)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const hour = String(d.getHours()).padStart(2, '0')
      const minute = String(d.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hour}:${minute}`
    },
    showPreview(url) {
      this.previewImage = url
    }
  }
}
</script>

<style scoped>
.detail-container {
  min-height: 100vh;
  background: #f5f5f5;
  padding-top: 70px;
}

.main-wrapper {
  max-width: 900px;
  margin: 30px auto;
  padding: 0 20px;
}

.back-link {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 20px;
  font-size: 14px;
  color: #3d8c40;
  cursor: pointer;
}

.back-icon {
  width: 16px;
  height: 16px;
}

.back-link:hover {
  text-decoration: underline;
}

.loading-box, .empty-box {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  background: #fff;
  border-radius: 8px;
  color: #999;
  font-size: 14px;
}

.question-card {
  background: #fff;
  border-radius: 8px;
  padding: 30px;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 20px;
}

.question-title {
  flex: 1;
  font-size: 15px;
  color: #333;
  line-height: 1.6;
}

.question-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.question-type {
  font-size: 14px;
  font-weight: 500;
}

.question-type.single {
  color: #3d8c40;
}

.question-type.multi {
  color: #e74c3c;
}

.favorite-icon {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 25px;
}

.option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px 18px;
  border-radius: 6px;
  background: #f9f9f9;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 58px;
  box-sizing: border-box;
}

.option:hover {
  background: #f0f7f0;
}

.option.selected {
  background: #d4edda;
  border: 1px solid #3d8c40;
}

.option.correct {
  background: #d4edda;
}

.option.correct .option-label {
  background: #3d8c40;
  color: #fff;
  border-color: #3d8c40;
}

.option.wrong {
  background: #f8d7da;
}

.option.wrong .option-label {
  background: #e74c3c;
  color: #fff;
  border-color: #e74c3c;
}

.option-label {
  width: 28px;
  height: 28px;
  border: 2px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #666;
  flex-shrink: 0;
}

.option-label.circle {
  border-radius: 50%;
}

.option-label.square {
  border-radius: 4px;
}

.option-text {
  font-size: 14px;
  color: #333;
}

.option-image {
  height: 40px;
  width: auto;
  object-fit: contain;
  border-radius: 4px;
  cursor: pointer;
}

/* 图片预览弹窗 */
.image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.preview-img {
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
  border-radius: 8px;
}

.confirm-multi {
  margin-top: 15px;
  margin-bottom: 25px;
  text-align: right;
}

.confirm-btn {
  padding: 8px 20px;
  background: #3d8c40;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.confirm-btn:hover {
  background: #2e7d32;
}

.answer-section {
  margin-bottom: 25px;
  padding: 18px;
  background: #fafafa;
  border-left: 3px solid #3d8c40;
  border-radius: 0 6px 6px 0;
}

.answer-info {
  display: flex;
  gap: 25px;
  margin-bottom: 12px;
  font-size: 14px;
}

.answer-label {
  color: #3d8c40;
}

.your-answer {
  color: #666;
}

.answer-explain {
  font-size: 14px;
  color: #666;
  line-height: 1.8;
}

.question-meta {
  display: flex;
  gap: 30px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #999;
}

.meta-icon {
  width: 16px;
  height: 16px;
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .main-wrapper {
    margin: 20px auto;
    padding: 0 15px;
  }

  .question-card {
    padding: 20px 15px;
  }

  .question-title {
    font-size: 15px;
  }

  .option {
    padding: 12px 15px;
  }

  .answer-info {
    flex-direction: column;
    gap: 8px;
  }

  .question-meta {
    flex-direction: column;
    gap: 10px;
  }
}
</style>
